///////////////////////////////////////////////////////////////////////////////////////////////////
/// @file   FleetMap.tsx
/// @brief  Agent world map: where license-server agents are logged in, their status, and red
///         paths wherever one entitlement (license key) is leased from more than one site.
///
/// @copyright Wheatstone Corporation 2026, All rights reserved.
///////////////////////////////////////////////////////////////////////////////////////////////////

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import apiClient from '../api/client';
import WorldMap, { linkKey } from '../components/fleetmap/WorldMap';
import type { AgentMap, AgentStatus, MapLink, MapMove, MapRefusal, MapSite } from '../components/fleetmap/types';
import '../components/fleetmap/fleet-map.css';

const WINDOWS = [
  { hours: 1, label: '1H' },
  { hours: 24, label: '24H' },
  { hours: 168, label: '7D' },
  { hours: 720, label: '30D' },
];
const REFRESH_MS = 30000;
const UNPLACED_PREVIEW = 6;

function readPref<T>(key: string, fallback: T): T {
  try {
    const v = localStorage.getItem(key);
    return v === null ? fallback : (JSON.parse(v) as T);
  } catch {
    return fallback;
  }
}
function writePref(key: string, value: unknown) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* private mode */ }
}

function ago(s: number): string {
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
  return `${Math.floor(s / 86400)}d ${Math.floor((s % 86400) / 3600)}h`;
}

/// Data API timestamps come back without a zone (UTC); pg ones as ISO.
function when(t: string | null): string {
  if (!t) return '';
  const iso = /[zZ]$|[+-]\d\d:?\d\d$/.test(t) ? t : `${t.replace(' ', 'T')}Z`;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? t : d.toLocaleString();
}

function agentLabel(name: string | null, id: number | null): string {
  if (id === null) return 'unknown agent';
  return `${name || 'agent'} #${id}`;
}

/// One line on what happened, for the alert list and panels.
function describeMove(m: MapMove): string {
  if (m.alarm_type === 'SEAT_HOP') {
    return `${m.seats ?? '?'} seat(s) freed by ${agentLabel(m.from_agent_name, m.from_agent_id)} were picked up by ${agentLabel(m.agent_name, m.agent_id)}`;
  }
  const how = m.via === 'checkout' ? 'released it, then checked it out again' : 'renewed its live lease';
  return `${agentLabel(m.agent_name, m.agent_id)} ${how} from the new site`;
}

/// Seat-demand CSV for sales: one row per license server and product.
function exportDemandCsv(rows: MapRefusal[], hours: number, siteName: (ip: string) => string) {
  const cell = (v: unknown) => {
    const t = v === null || v === undefined ? '' : String(v);
    return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
  };
  const header = ['account', 'product', 'seats_in_use', 'total_seats', 'seats_requested', 'max_shortfall',
    'refusal_count', 'first_refused', 'last_refused', 'status', 'satisfied_at', 'site', 'ip', 'agent', 'agent_id'];
  const lines = rows.map((r) => [
    r.customer_username, r.product_name, r.seats_in_use, r.total_seats, r.seats_requested, r.max_shortfall,
    r.refusal_count, r.first_at, r.last_at, r.live ? 'refused now' : r.satisfied_at ? 'satisfied' : 'lapsed',
    r.satisfied_at, siteName(r.ip), r.ip, r.agent_name, r.agent_id,
  ].map(cell).join(','));
  const blob = new Blob([[header.join(','), ...lines].join('\r\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `seat-demand-${hours}h-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function place(site: MapSite | undefined, ip: string): string {
  if (!site) return ip;
  const g = site.geo;
  return [g.city, g.region && g.region !== g.city ? g.region : null, g.country].filter(Boolean).join(', ') || ip;
}

/// Ticks once a second on its own, so the clock never re-renders the map.
function HudClock({ fetchedAt, error, acquiring }: { fetchedAt: number; error: boolean; acquiring: boolean }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const syncAge = fetchedAt ? Math.max(0, Math.floor((now - fetchedAt) / 1000)) : null;
  return (
    <>
      <span className="fm-clock">UTC {new Date(now).toISOString().slice(11, 19)}</span>
      <span className={`fm-sync ${error ? 'fm-txt-red' : ''}`}>
        {error ? 'LINK LOST' : acquiring ? 'ACQUIRING…' : syncAge !== null ? `SYNC T-${syncAge}s` : ''}
      </span>
    </>
  );
}

function Stat({ label, value, sub, tone, testId }: { label: string; value: string | number; sub?: string; tone?: string; testId?: string }) {
  return (
    <div className={`fm-stat ${tone ? `fm-stat-${tone}` : ''}`}>
      <div className="fm-stat-label">{label}</div>
      <div className="fm-stat-value" data-testid={testId}>{value}</div>
      {sub && <div className="fm-stat-sub">{sub}</div>}
    </div>
  );
}

export default function FleetMap() {
  const [hours, setHours] = useState<number>(() => readPref('fleetmap.hours', 24));
  const [showAccountLinks, setShowAccountLinks] = useState<boolean>(() => readPref('fleetmap.accountLinks', true));
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [data, setData] = useState<AgentMap | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetchedAt, setFetchedAt] = useState<number>(0);
  const [lowPower, setLowPower] = useState<boolean>(() => readPref('fleetmap.lowPower', false));
  const [hidden, setHidden] = useState(() => typeof document !== 'undefined' && document.hidden);
  const [selectedIp, setSelectedIp] = useState<string | null>(null);
  const [selectedLink, setSelectedLink] = useState<string | null>(null);
  const [showAllUnplaced, setShowAllUnplaced] = useState(false);

  // Loads can overlap (window change mid-request, slow poll); only the most
  // recently started one may touch state.
  const requestSeq = useRef(0);
  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      const d = await apiClient.getAgentMap(hours);
      if (seq !== requestSeq.current) return;
      setData(d);
      setError(null);
      setFetchedAt(Date.now());
    } catch (e) {
      if (seq !== requestSeq.current) return;
      setError(e instanceof Error ? e.message : 'Failed to load agent map');
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [hours]);

  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  // A hidden tab keeps its CSS animations ticking in some browsers; pause them.
  useEffect(() => {
    const onVis = () => setHidden(document.hidden);
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  const customers = useMemo(() => {
    const m = new Map<number, string>();
    data?.sites.forEach((s) => s.customers.forEach((c) => m.set(c.id, c.username)));
    data?.links.forEach((l) => m.set(l.customer_id, l.customer_username));
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [data]);

  // Customer filter: that account's agents, plus the far ends of its links.
  // Per-site status, accounts and moved flag are recomputed from what is
  // left, so a NAT site shared by several accounts shows only this one.
  const view = useMemo(() => {
    if (!data) return { sites: [] as MapSite[], links: [] as MapLink[], demand: [] as MapRefusal[] };
    if (customerId === null) return { sites: data.sites, links: data.links, demand: data.demand };
    const links = data.links.filter((l) => l.customer_id === customerId);
    const demand = data.demand.filter((d) => d.customer_id === customerId);
    const ips = new Set([...links.flatMap((l) => [l.a, l.b]), ...demand.filter((d) => d.live).map((d) => d.ip)]);
    const movedIps = new Set(links.filter((l) => l.kind === 'license_move').flatMap((l) => [l.a, l.b]));
    const refusedIps = new Set(demand.filter((d) => d.live).map((d) => d.ip));
    const rank: Record<AgentStatus, number> = { online: 0, idle: 1, offline: 2 };
    const sites = data.sites
      .filter((s) => ips.has(s.ip) || s.agents.some((a) => a.customer_id === customerId))
      .map((s) => {
        const agents = s.agents.filter((a) => a.customer_id === customerId);
        const status = agents.reduce<AgentStatus>((best, a) => (rank[a.status] < rank[best] ? a.status : best), 'offline');
        return {
          ...s,
          agents,
          status,
          customers: s.customers.filter((c) => c.id === customerId),
          moved: movedIps.has(s.ip),
          refused: refusedIps.has(s.ip),
        };
      });
    return { sites, links, demand };
  }, [data, customerId]);

  const siteByIp = useMemo(() => new Map(view.sites.map((s) => [s.ip, s])), [view.sites]);

  // A refresh or window change can drop the filtered account: the select
  // would show ALL ACCOUNTS over an empty filtered map.
  useEffect(() => {
    if (data && customerId !== null && !customers.some(([id]) => id === customerId)) setCustomerId(null);
  }, [data, customers, customerId]);

  // A refresh, window change or filter can drop the selected site or path;
  // a dangling selection would dim the whole map.
  useEffect(() => {
    if (selectedIp && !siteByIp.has(selectedIp)) setSelectedIp(null);
    if (selectedLink && !view.links.some((l) => linkKey(l) === selectedLink)) setSelectedLink(null);
  }, [siteByIp, view.links, selectedIp, selectedLink]);
  const moveLinks = view.links.filter((l) => l.kind === 'license_move');
  // Every active move, newest first, with the path it belongs to.
  const moves = moveLinks
    .flatMap((l) => l.moves.map((m) => ({ m, key: linkKey(l), customer: l.customer_username })))
    .sort((x, y) => y.m.alarm_id - x.m.alarm_id);
  // Seat demand: live refusals first, then by most recent. A live refusal
  // whose seats are held at another site also has a yellow path to open.
  const demand = [...view.demand].sort((x, y) => Number(y.live) - Number(x.live) || x.last_age_s - y.last_age_s);
  const refusalPath = (r: MapRefusal) => view.links.find((l) => l.kind === 'refused'
    && l.refusals.some((x) => x.agent_id === r.agent_id && x.entitlement_id === r.entitlement_id));
  const unplaced = view.sites.filter((s) => s.geo.lat === null);
  const selectedSite = selectedIp ? siteByIp.get(selectedIp) : undefined;
  const activeLink = selectedLink ? view.links.find((l) => linkKey(l) === selectedLink) : undefined;
  // Cards follow the account filter; alarms are only counted globally, so
  // those cards say so while a filter is active.
  const s = useMemo(() => {
    if (!data) return undefined;
    if (customerId === null) return data.stats;
    const agents = view.sites.flatMap((x) => x.agents);
    const filteredMoves = view.links.flatMap((l) => l.moves);
    return {
      ...data.stats,
      agents: agents.length,
      online: agents.filter((a) => a.status === 'online').length,
      idle: agents.filter((a) => a.status === 'idle').length,
      offline: agents.filter((a) => a.status === 'offline').length,
      sites: view.sites.length,
      placed_sites: view.sites.filter((x) => x.geo.lat !== null).length,
      countries: new Set(view.sites.map((x) => x.geo.country).filter(Boolean)).size,
      customers: new Set([...agents.map((a) => a.customer_id), ...view.links.map((l) => l.customer_id)]).size,
      // Server-side total: includes leases whose agent aged out of the window.
      seats_in_use: data.stats.seats_by_customer[String(customerId)] ?? 0,
      move_alarms: filteredMoves.filter((m) => m.alarm_type === 'LICENSE_SITE_MOVE').length,
      seat_hop_alarms: filteredMoves.filter((m) => m.alarm_type === 'SEAT_HOP').length,
      move_customers: filteredMoves.length > 0 ? 1 : 0,
      refused_agents: new Set(view.demand.filter((d) => d.live).map((d) => d.agent_id)).size,
      demand_customers: view.demand.length > 0 ? 1 : 0,
    };
  }, [data, view, customerId]);
  const alarmScope = customerId === null ? '' : ' (ALL)';

  const selectHours = (h: number) => { setHours(h); writePref('fleetmap.hours', h); };
  const toggleAccountLinks = () => {
    const next = !showAccountLinks;
    setShowAccountLinks(next);
    writePref('fleetmap.accountLinks', next);
    // Hiding the layer must not leave a hidden path selected (panel + dimming).
    if (!next && activeLink?.kind === 'same_account') setSelectedLink(null);
  };

  return (
    <div className={`fm-root ${lowPower ? 'fm-still' : ''} ${hidden ? 'fm-paused' : ''}`} data-testid="fleet-map">
      {/* HUD header */}
      <div className="fm-hud">
        <div className="fm-title">
          <span className={`fm-live ${error ? 'fm-live-err' : ''}`} />
          <h1>AGENT GRID</h1>
          <span className="fm-subtitle">// GLOBAL LICENSE TELEMETRY</span>
        </div>
        <div className="fm-controls">
          <HudClock fetchedAt={fetchedAt} error={!!error} acquiring={loading && !data} />
          <div className="fm-seg" role="group" aria-label="Time window">
            {WINDOWS.map((w) => (
              <button key={w.hours} type="button" className={hours === w.hours ? 'on' : ''} aria-pressed={hours === w.hours} onClick={() => selectHours(w.hours)}>
                {w.label}
              </button>
            ))}
          </div>
          <select
            className="fm-select"
            value={customerId ?? ''}
            onChange={(e) => { setCustomerId(e.target.value ? Number(e.target.value) : null); setSelectedIp(null); setSelectedLink(null); }}
            aria-label="Filter by account"
          >
            <option value="">ALL ACCOUNTS</option>
            {customers.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
          <button type="button" className={`fm-toggle ${showAccountLinks ? 'on' : ''}`} aria-pressed={showAccountLinks} onClick={toggleAccountLinks}>
            ACCOUNT LINKS
          </button>
          <button
            type="button"
            className={`fm-toggle ${lowPower ? 'on' : ''}`}
            aria-pressed={lowPower}
            title="Freeze all map animation (lighter on the GPU, e.g. alongside video)"
            onClick={() => { setLowPower((v) => { writePref('fleetmap.lowPower', !v); return !v; }); }}
          >
            LOW POWER
          </button>
          <button type="button" className="fm-toggle" onClick={load} disabled={loading} aria-label="Refresh">⟳</button>
        </div>
      </div>

      {/* Telemetry readouts */}
      <div className="fm-stats">
        <Stat label="AGENTS ONLINE" value={s ? s.online : '—'} sub={s && data ? `${s.agents} seen in ${WINDOWS.find((w) => w.hours === data.window_hours)?.label ?? `${data.window_hours}H`}` : undefined} tone="online" testId="fm-online" />
        <Stat label="IDLE" value={s ? s.idle : '—'} tone="idle" />
        <Stat label="OFFLINE" value={s ? s.offline : '—'} tone="offline" />
        <Stat label="SITES" value={s ? s.sites : '—'} sub={s ? `${s.placed_sites} located · ${s.countries} countries` : undefined} />
        <Stat label="ACCOUNTS" value={s ? s.customers : '—'} />
        <Stat
          label="SEATS LEASED"
          value={s ? s.seats_in_use : '—'}
          sub={s && s.refused_agents > 0 ? `${s.refused_agents} being refused more seats` : undefined}
          tone={s && s.refused_agents > 0 ? 'idle' : undefined}
          testId="fm-seats"
        />
        <Stat
          label="LICENSE MOVES"
          value={s ? s.move_alarms + s.seat_hop_alarms : '—'}
          sub={s ? (s.move_alarms + s.seat_hop_alarms ? `${s.move_alarms} moved · ${s.seat_hop_alarms} seat hop` : 'NOMINAL') : undefined}
          tone={s && s.move_alarms + s.seat_hop_alarms > 0 ? 'red' : 'online'}
          testId="fm-moves"
        />
        <Stat
          label={`ACTIVE ALARMS${alarmScope}`}
          value={s ? s.active_alarms : '—'}
          sub={s ? 'moves judged every 5 min' : undefined}
          tone={s && s.active_alarms > 0 ? 'red' : undefined}
        />
      </div>

      {error && <div className="fm-error">SIGNAL ERROR — {error}</div>}
      {data?.truncated && (
        <div className="fm-error fm-warn">PARTIAL PICTURE — row cap reached; showing the newest agents and largest lease groups only.</div>
      )}

      <div className="fm-body">
        <div className="fm-map-wrap">
          <WorldMap
            sites={view.sites}
            links={view.links}
            showAccountLinks={showAccountLinks}
            selectedIp={selectedIp}
            selectedLinkKey={selectedLink}
            onSelectSite={setSelectedIp}
            onSelectLink={setSelectedLink}
          />
          {data && view.sites.length === 0 && (
            <div className="fm-empty">NO AGENTS REPORTING IN THIS WINDOW</div>
          )}
        </div>

        <aside className="fm-side">
          {selectedSite ? (
            <section className="fm-panel" data-testid="fm-site-panel">
              <header>
                <span>TARGET LOCK</span>
                <button type="button" onClick={() => setSelectedIp(null)} aria-label="Close">✕</button>
              </header>
              <div className="fm-target-name">{place(selectedSite, selectedSite.ip)}</div>
              <dl className="fm-kv">
                <dt>IP</dt><dd>{selectedSite.ip}</dd>
                {selectedSite.geo.org && <><dt>NET</dt><dd>{selectedSite.geo.org}</dd></>}
                {selectedSite.geo.lat !== null && <><dt>POS</dt><dd>{selectedSite.geo.lat.toFixed(2)}, {selectedSite.geo.lon!.toFixed(2)}</dd></>}
                <dt>STATUS</dt><dd className={`fm-txt-${selectedSite.status}`}>{selectedSite.status.toUpperCase()}</dd>
              </dl>
              {selectedSite.moved && (
                <div className="fm-alert-box">
                  <b>⚠ LICENSE MOVED</b>
                  {moves.filter(({ m }) => m.from_ip === selectedSite.ip || m.to_ip === selectedSite.ip).map(({ m }) => {
                    const out = m.from_ip === selectedSite.ip;
                    const other = out ? m.to_ip : m.from_ip;
                    return (
                      <div key={m.alarm_id}>
                        {out ? '→ to' : '← from'} {place(siteByIp.get(other), other)} — {m.product_name ?? 'product'}{m.alarm_type === 'SEAT_HOP' ? ' (seat hop)' : ''}
                      </div>
                    );
                  })}
                </div>
              )}
              {selectedSite.refused && (
                <div className="fm-warn-box">
                  <b>⚠ REFUSED MORE SEATS</b>
                  {demand.filter((r) => r.live && r.ip === selectedSite.ip).map((r) => (
                    <div key={`${r.agent_id}|${r.entitlement_id}`}>
                      {agentLabel(r.agent_name, r.agent_id)} wants {r.seats_requested} {r.product_name ?? 'seat'}: {r.seats_in_use}/{r.total_seats} in use{r.holder_ips.length ? ` at ${r.holder_ips.map((h) => place(siteByIp.get(h), h)).join(', ')}` : ' here'}; short by {r.max_shortfall}
                    </div>
                  ))}
                </div>
              )}
              <div className="fm-sub-h">AGENTS [{selectedSite.agents.length}]</div>
              <ul className="fm-agents">
                {selectedSite.agents.map((a) => (
                  <li key={a.id}>
                    <div className="fm-agent-top">
                      <i className={`fm-dotk fm-bg-${a.status}`} />
                      <b>{a.name || 'agent'} #{a.id}</b>
                      <span className="fm-agent-age">{ago(a.last_seen_age_s)} ago</span>
                    </div>
                    <div className="fm-agent-meta">
                      <span className="fm-acct">{a.customer_username}</span>
                      {a.server_version && <span>v{a.server_version}</span>}
                      <span>{a.active_leases} lease{a.active_leases === 1 ? '' : 's'} · {a.seats_checked_out} seat{a.seats_checked_out === 1 ? '' : 's'}</span>
                    </div>
                    {a.products.length > 0 && <div className="fm-agent-products">{a.products.join(' · ')}</div>}
                    <div className="fm-agent-uuid" title="dev_uuid">{a.dev_uuid}</div>
                  </li>
                ))}
                {selectedSite.agents.length === 0 && <li className="fm-muted">No agent in window — lease IP only</li>}
              </ul>
            </section>
          ) : activeLink ? (
            <section className="fm-panel" data-testid="fm-link-panel">
              <header>
                <span>{activeLink.kind === 'license_move' ? 'LICENSE MOVE' : activeLink.kind === 'refused' ? 'REFUSED · SEATS HELD ELSEWHERE' : 'ACCOUNT PATH'}</span>
                <button type="button" onClick={() => setSelectedLink(null)} aria-label="Close">✕</button>
              </header>
              <div className="fm-target-name">{activeLink.customer_username}</div>
              {(() => {
                // Old site on the left: for a move, the latest move's origin.
                const from = activeLink.kind === 'license_move' && activeLink.moves[0] ? activeLink.moves[0].from_ip
                  : activeLink.kind === 'refused' && activeLink.refusals[0] ? (activeLink.refusals[0].ip === activeLink.a ? activeLink.b : activeLink.a)
                  : activeLink.a;
                const to = from === activeLink.a ? activeLink.b : activeLink.a;
                return (
                  <div className="fm-path">
                    <button type="button" onClick={() => { setSelectedLink(null); setSelectedIp(from); }}>{place(siteByIp.get(from), from)}<small>{from}</small></button>
                    <span className={activeLink.kind === 'license_move' ? 'fm-txt-red' : activeLink.kind === 'refused' ? 'fm-txt-yellow' : 'fm-txt-cyan'}>{activeLink.kind === 'same_account' ? '⇄' : '→'}</span>
                    <button type="button" onClick={() => { setSelectedLink(null); setSelectedIp(to); }}>{place(siteByIp.get(to), to)}<small>{to}</small></button>
                  </div>
                );
              })()}
              {activeLink.kind === 'license_move' ? (
                <>
                  <div className="fm-sub-h">ACTIVE ALARMS [{activeLink.moves.length}]</div>
                  <ul className="fm-ents">
                    {activeLink.moves.map((m) => (
                      <li key={m.alarm_id}>
                        <b>{m.alarm_type === 'SEAT_HOP' ? 'SEAT HOP' : 'MOVED'} · {m.product_name ?? 'product'}</b>
                        <span>{describeMove(m)}.</span>
                        <span>{m.from_site ?? m.from_ip} → {m.to_site ?? m.to_ip}</span>
                        <span>{m.trip_count > 1 ? `seen ${m.trip_count}× · ` : ''}last {when(m.last_at)} · clear in Entitlements › Alarms</span>
                      </li>
                    ))}
                  </ul>
                </>
              ) : activeLink.kind === 'refused' ? (
                <>
                  <div className="fm-sub-h">BEING REFUSED [{activeLink.refusals.length}]</div>
                  <ul className="fm-ents">
                    {activeLink.refusals.map((r) => (
                      <li key={`${r.agent_id}|${r.entitlement_id}`}>
                        <b>{r.product_name ?? 'product'} · {r.seats_in_use}/{r.total_seats} seats in use</b>
                        <span>{agentLabel(r.agent_name, r.agent_id)} at the right-hand site asked for {r.seats_requested} and was refused; the pool is held at {r.holder_ips.map((h) => place(siteByIp.get(h), h)).join(', ')}.</span>
                        <span>{r.refusal_count > 1 ? `refused ${r.refusal_count}× since ${when(r.first_at)} · ` : ''}last {ago(r.last_age_s)} ago</span>
                        <div className="fm-bar fm-bar-y"><i style={{ width: `${Math.min(100, (r.seats_in_use / Math.max(1, r.total_seats)) * 100)}%` }} /></div>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <p className="fm-muted">Same account credentials in use at both sites. Sharing an account's seats across sites is normal; nothing has moved between them.</p>
              )}
            </section>
          ) : (
            <section className="fm-panel" data-testid="fm-alerts">
              <header><span>LICENSE MOVE ALERTS</span><span className={moves.length ? 'fm-txt-red' : 'fm-txt-online'}>{moves.length}</span></header>
              {moves.length === 0 ? (
                <p className="fm-muted">No license has moved between sites. Seats shared across sites under one account are normal and not flagged.</p>
              ) : (
                <ul className="fm-alerts">
                  {moves.map(({ m, key, customer }) => (
                    <li key={m.alarm_id}>
                      <button type="button" onClick={() => setSelectedLink(key)}>
                        <div className="fm-alert-acct">{customer} · {m.alarm_type === 'SEAT_HOP' ? 'SEAT HOP' : 'MOVED'}</div>
                        <div className="fm-alert-path">
                          {place(siteByIp.get(m.from_ip), m.from_ip)} <span className="fm-txt-red">→</span> {place(siteByIp.get(m.to_ip), m.to_ip)}
                        </div>
                        <div className="fm-alert-prod">{m.product_name ?? 'product'}{m.trip_count > 1 ? ` · ${m.trip_count}×` : ''} · {when(m.last_at)}</div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          {!selectedSite && !activeLink && demand.length > 0 && (
            <section className="fm-panel" data-testid="fm-demand">
              <header>
                <span className="fm-txt-yellow">SEAT DEMAND · {WINDOWS.find((w) => w.hours === data?.window_hours)?.label ?? ''}</span>
                <span className="fm-demand-actions">
                  <span className="fm-txt-yellow">{demand.length}</span>
                  <button type="button" className="fm-csv" onClick={() => exportDemandCsv(demand, data?.window_hours ?? hours, (ip) => place(siteByIp.get(ip), ip))} data-testid="fm-demand-csv">CSV</button>
                </span>
              </header>
              <ul className="fm-alerts fm-refusals">
                {demand.map((r) => {
                  const path = r.live ? refusalPath(r) : undefined;
                  return (
                    <li key={`${r.agent_id}|${r.entitlement_id}`}>
                      <button type="button" onClick={() => (path ? setSelectedLink(linkKey(path)) : setSelectedIp(r.ip))}>
                        <div className="fm-alert-acct">
                          {r.live ? <span className="fm-live-tag">LIVE</span> : null}
                          {r.customer_username} · {r.product_name ?? 'product'} · needs +{r.max_shortfall}
                        </div>
                        <div className="fm-alert-path">
                          {place(siteByIp.get(r.ip), r.ip)}: {r.seats_in_use}/{r.total_seats} seats in use
                          {path && r.holder_ips.length ? <> · held at {place(siteByIp.get(r.holder_ips[0]), r.holder_ips[0])}{r.holder_ips.length > 1 ? ` +${r.holder_ips.length - 1}` : ''}</> : null}
                        </div>
                        <div className="fm-alert-prod">
                          {agentLabel(r.agent_name, r.agent_id)} refused {r.refusal_count}× since {when(r.first_at)}
                          {r.live ? ` · last ${ago(r.last_age_s)} ago` : r.satisfied_at ? ` · got seats ${when(r.satisfied_at)}` : ` · last ${when(r.last_at)}`}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {unplaced.length > 0 && (
            <section className="fm-panel">
              <header><span>UNPLACED NODES</span><span>{unplaced.length}</span></header>
              <ul className="fm-unplaced">
                {(showAllUnplaced ? unplaced : unplaced.slice(0, UNPLACED_PREVIEW)).map((u) => (
                  <li key={u.ip}>
                    <button type="button" onClick={() => { setSelectedLink(null); setSelectedIp(u.ip); }}>
                      <i className={`fm-dotk fm-bg-${u.status}`} />
                      <span>{u.ip}</span>
                      <small>{u.geo.private ? 'PRIVATE' : 'NO FIX'} · {u.agents.length} agent{u.agents.length === 1 ? '' : 's'}{u.moved ? ' · MOVED' : ''}</small>
                    </button>
                  </li>
                ))}
              </ul>
              {unplaced.length > UNPLACED_PREVIEW && (
                <button type="button" className="fm-more" onClick={() => setShowAllUnplaced((v) => !v)}>
                  {showAllUnplaced ? 'COLLAPSE' : `SHOW ALL (${unplaced.length})`}
                </button>
              )}
            </section>
          )}
        </aside>
      </div>
    </div>
  );
}
